import { useEffect } from "react";
import { connect } from "react-redux";
import { Field, formValueSelector, reduxForm } from "redux-form";
import {
  Typography,
  FormControl,
  FormGroup,
  FormLabel,
  Button,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { TextInput, CheckboxInput, SelectInput } from "../FormInputs";
import { ModuleInfo } from "../../../api/modules";
import { APIConfig, KEEGROVE_DEFAULT_ENDPOINT } from "../../../api/clients";

const frequencyOptions = {
  minute15: "Every 15 minutes",
  hour1: "Hourly",
  hour2: "Every 2 hours",
  hour4: "Every 4 hours",
  hour6: "Every 6 hours",
  hour12: "Every 12 hours",
  day1: "Daily",
};

export type ClientAccessKey = `access_${number}`;
export type ClientFormValue = {
  id: number;
  name: string;
  directory: string;
  api?: APIConfig["api"];
  syncFrequency?: keyof typeof frequencyOptions;
  endpoint?: string;
  apiKey?: string;
} & Record<ClientAccessKey, boolean>;

const validate = (values: ClientFormValue) => {
  const errors: Partial<Record<keyof ClientFormValue, string>> = {};
  if (!values.name) {
    errors.name = "Required";
  }
  if (values.api && !values.syncFrequency) {
    errors.syncFrequency = "Required when an external API is selected";
  }
  if (values.api === "keegrove") {
    if (!values.apiKey) {
      errors.apiKey = "Required for KeeGrove";
    }
    if (!values.endpoint) {
      errors.endpoint = "Required for KeeGrove";
    }
  }
  return errors;
};

type FormProps = {
  handleSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  error?: string;
  valid?: boolean;
  pristine?: boolean;
  submitting?: boolean;
  isValid: (valid: boolean) => void;
  isWaiting: (waiting: boolean) => void;
  initialValues?: Partial<ClientFormValue>;
  permissions?: ModuleInfo[];
  onTestKeegrove?: () => void;
  testingKeegrove?: boolean;
  // injected by connect
  selectedApi?: APIConfig["api"];
};

const ClientForm = ({
  handleSubmit,
  error,
  valid,
  pristine,
  submitting,
  isValid,
  isWaiting,
  initialValues,
  permissions,
  onTestKeegrove,
  testingKeegrove,
  selectedApi,
}: FormProps) => {
  useEffect(() => {
    isValid(!pristine && !!valid && !submitting);
    isWaiting(!!submitting);
  });

  const showKeegrove = selectedApi === "keegrove";
  const canTestKeegrove =
    showKeegrove && !!onTestKeegrove && !!initialValues?.id && !!pristine;

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Field
        component={TextInput}
        name="name"
        id="name"
        autoFocus={!initialValues}
        label="Name"
      />
      <Field
        component={TextInput}
        name="directory"
        id="directory"
        label="Irrimax Directory"
      />
      <FormControl component="fieldset">
        <FormLabel component="legend">Permissions</FormLabel>
        <FormGroup row>
          {permissions &&
            permissions.map((permission, index) => {
              return (
                <Field
                  key={`access_${index}`}
                  name={`access_${permission.id}`}
                  id={`access_${permission.id}`}
                  component={CheckboxInput}
                  label={permission.name}
                />
              );
            })}
        </FormGroup>
      </FormControl>

      <Field
        component={SelectInput}
        name="api"
        id="api"
        label="External API"
        allowEmpty
        options={{
          costa: "Costa",
          keegrove: "KeeGrove (Haygrove)",
        }}
      />
      <Field
        component={SelectInput}
        name="syncFrequency"
        id="syncFrequency"
        label="Sync Frequency"
        allowEmpty
        options={frequencyOptions}
      />

      {showKeegrove && (
        <Box mt={2}>
          <Typography variant="subtitle1">KeeGrove configuration</Typography>
          <Field
            component={TextInput}
            name="endpoint"
            id="endpoint"
            label="API URL"
            helperText={`Default test: ${KEEGROVE_DEFAULT_ENDPOINT}. Live: https://keegrove.haygrove.com:8069/api/SupplierData`}
          />
          <Field
            component={TextInput}
            name="apiKey"
            id="apiKey"
            label="API Key (X-Api-Key)"
            type="password"
          />
          <Typography variant="caption" color="textSecondary" display="block">
            All of this client's fields will be sent using their local field id
            as the KeeGrove <code>field_id</code>.
          </Typography>

          {onTestKeegrove && (
            <Box mt={2}>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                disabled={!canTestKeegrove || testingKeegrove}
                onClick={onTestKeegrove}
                startIcon={
                  testingKeegrove ? <CircularProgress size={16} /> : undefined
                }
              >
                Send last hour to KeeGrove
              </Button>
              {!pristine && (
                <Typography variant="caption" color="textSecondary" display="block">
                  Save your changes before testing.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      <Typography variant="body1" gutterBottom color="error">
        <b>{error}</b>
      </Typography>
    </form>
  );
};

const selector = formValueSelector("ClientForm");
const mapStateToProps = (state: object) => ({
  selectedApi: selector(state, "api") as APIConfig["api"] | undefined,
});

export default connect(mapStateToProps)(
  reduxForm<ClientFormValue, FormProps>({
    form: "ClientForm",
    validate,
    enableReinitialize: true,
  })(ClientForm)
);
